/*
 * Reputation: how passengers rate the airport, in five categories, and the online buzz around
 * it. Every departure (and every cancellation) moves the categories; the overall rating is their
 * weighted mean. Buzz swings with notable events and calms down by the hour: a storm of angry
 * posts (a social media crisis) scares airlines off, praise going viral brings them in.
 */

/** Rating categories. Saved: append only. */
export const RatingCategory = {
  Punctuality: 0,
  Waiting: 1,
  Comfort: 2,
  Prices: 3,
  Reliability: 4,
} as const;
export type RatingCategoryId = (typeof RatingCategory)[keyof typeof RatingCategory];
export const RATING_CATEGORIES = 5;

/** Weights (percent) of the categories in the overall rating. */
const WEIGHTS: readonly number[] = [30, 20, 15, 15, 20];

/** Each new experience moves a category this fraction (1/n) of the way. */
const SMOOTHING = 8;

export const INITIAL_CATEGORY = 600;

/** Buzz below this is a social media crisis; above the other a wave of praise. */
export const CRISIS_BUZZ = -400;
export const VIRAL_BUZZ = 400;
const MAX_BUZZ = 1000;

export interface ReputationWorld {
  ratings: number[];
  rating: number;
  buzz: number;
}

/** One passenger experience (a departure): 0–1000 per category, -1 for "no opinion". */
export function rate(w: ReputationWorld, scores: readonly number[]): void {
  for (let i = 0; i < RATING_CATEGORIES; i++) {
    const s = scores[i] ?? -1;
    if (s < 0) continue;
    const r = w.ratings[i] ?? INITIAL_CATEGORY;
    w.ratings[i] = r + Math.trunc((Math.max(0, Math.min(1000, s)) - r) / SMOOTHING);
  }
  overall(w);
}

/** A blow (or boost) straight to one category, e.g. a cancelled flight. */
export function hit(w: ReputationWorld, category: RatingCategoryId, points: number): void {
  w.ratings[category] = Math.max(0, Math.min(1000, (w.ratings[category] ?? 0) + points));
  overall(w);
}

function overall(w: ReputationWorld): void {
  let sum = 0;
  for (let i = 0; i < RATING_CATEGORIES; i++) sum += (w.ratings[i] ?? 0) * (WEIGHTS[i] ?? 0);
  w.rating = Math.floor(sum / 100);
}

/** Moves the buzz: positive for praise, negative for complaints. */
export function stir(w: ReputationWorld, amount: number): void {
  w.buzz = Math.max(-MAX_BUZZ, Math.min(MAX_BUZZ, w.buzz + amount));
}

/** Hourly: the talk dies down by a tenth. */
export function calmDown(w: ReputationWorld): void {
  w.buzz -= Math.trunc(w.buzz / 10);
}

/** Airline offer rate per mille from the buzz: halved in a crisis, up a third when viral. */
export function buzzOfferPermille(buzz: number): number {
  if (buzz <= CRISIS_BUZZ) return 500;
  if (buzz >= VIRAL_BUZZ) return 1300;
  return 1000;
}

/** Price score: cheap is loved, the standard tariff is fine, double is resented. */
export function priceScore(paxFeePct: number): number {
  return Math.max(0, Math.min(1000, 1000 - (paxFeePct - 50) * 6));
}
